import { atom } from 'jotai'

// Currency atom - defaults to USD, can be updated when ticket options load
export const currencyAtom = atom<string>('USD')

// Types
export interface TicketItem {
    id: string
    name: string
    price: number
    category: string // 'vip' | 'standard' | 'economy'
    date: string
    imageUrl?: string
}

export interface CartItem {
    ticket: TicketItem
    quantity: number
    /** USHOP-2914: early-bird for an eligible customer — qty fixed at 1, no stepper */
    locked?: boolean
}

// Atoms
export const cartItemsAtom = atom<CartItem[]>([])
// Track cart owner (customer id)
export const cartOwnerIdAtom = atom<string | null>(null)

// Derived: Total price
export const cartTotalAtom = atom(get => {
    const items = get(cartItemsAtom)
    return items.reduce((total, item) => total + item.ticket.price * item.quantity, 0)
})

// Derived: Total items count
export const cartCountAtom = atom(get => {
    const items = get(cartItemsAtom)
    return items.reduce((total, item) => total + item.quantity, 0)
})

// Actions
export const addToCartAtom = atom(null, (get, set, ticket: TicketItem, quantity: number = 1) => {
    const currentItems = get(cartItemsAtom)
    const existingIndex = currentItems.findIndex(item => item.ticket.id === ticket.id)

    if (existingIndex >= 0) {
        // Update quantity
        const newItems = [...currentItems]
        const existingItem = newItems[existingIndex]
        if (existingItem) {
            newItems[existingIndex] = {
                ...existingItem,
                quantity: existingItem.quantity + quantity
            }
        }
        set(cartItemsAtom, newItems)
    } else {
        // Add new item
        set(cartItemsAtom, [...currentItems, { ticket, quantity }])
    }
})

export const removeFromCartAtom = atom(null, (get, set, ticketId: string) => {
    const currentItems = get(cartItemsAtom)
    set(
        cartItemsAtom,
        currentItems.filter(item => item.ticket.id !== ticketId)
    )
})

export const updateQuantityAtom = atom(null, (get, set, ticketId: string, quantity: number) => {
    if (quantity < 0) {
        // Remove item if quantity is negative
        const currentItems = get(cartItemsAtom)
        set(
            cartItemsAtom,
            currentItems.filter(item => item.ticket.id !== ticketId)
        )
        return
    }

    const currentItems = get(cartItemsAtom)
    const newItems = currentItems.map(item => (item.ticket.id === ticketId ? { ...item, quantity } : item))
    set(cartItemsAtom, newItems)
})

// Set cart items directly (used for category-based selection)
export const setCartItemsAtom = atom(null, (_get, set, items: CartItem[]) => {
    set(cartItemsAtom, items)
})

export const clearCartAtom = atom(null, (_get, set) => {
    set(cartItemsAtom, [])
})
